import _ from 'lodash';

import OhsApiRequest from 'global-services/api/OhsApiRequest';
import { ModuleType } from 'global-services/constants/OhsObject';
import { ApiResListsType, OhsApiRequestName, RequestType } from 'global-services/api/OhsApiModels';
import { OhsHrLookup } from 'human-resource/models/OhsHumanResourceModels';
import { parseHrLookupList } from 'human-resource/OhsHumanResourceServices';
import { fetchCusvalValue } from 'global-components/form/cusvals/OhsCusvalServices';
import { OhsCusval } from 'global-components/form/cusvals/OhsCusvalModel';
import { OhsConfiguredLocationsByTier } from 'global-components/form/fields/geoLocation/OhsGeoLocationModels';
import OhsFetchAll from 'global-services/OhsFetchAll';

import OhsIncidentTaskRecord from './models/OhsIncidentTaskRecord';
import OhsIncidentRecord from './OhsIncidentRecord';
import { OhsIncidentFilterPayload, OhsIncidentSearchMain } from './OhsIncidentModels';
import { IncidentLookUp } from './IncidentLookUpModel';

export const setBoolean = (val: any) => {
  const isBoolean = _.isBoolean(val);
  if (isBoolean) {
    if (val) return 'Yes';
    return 'No';
  }
  return val;
};

export const getHrList = (filter?: any): Promise<ApiResListsType<OhsHrLookup[]> | null> => {
  return OhsApiRequest(RequestType.Module, ModuleType.Incident, OhsApiRequestName.HRLookup, {
    filter,
  }).then((res: any) => {
    if (!res || !res.items) return null;
    res.items = parseHrLookupList(res?.items || []);
    return res;
  });
};

export const getAllHrList = async () => {
  const res = await OhsFetchAll(getHrList, { archived: false });
  if (!res) return null;
  return res?.items || [];
};

export const listIncidentLocations = (): Promise<OhsConfiguredLocationsByTier | null> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.Incident,
    OhsApiRequestName.ListConfiguredLocations
  );
};

export const addIncident = (payload: any) => {
  return OhsApiRequest(RequestType.Module, ModuleType.Incident, OhsApiRequestName.Create, payload);
};

export const editIncident = (payload: any) => {
  return OhsApiRequest(RequestType.Module, ModuleType.Incident, OhsApiRequestName.Edit, payload);
};

export const getIncident = async (
  _id: string,
  disableErrorToast?: boolean
): Promise<OhsIncidentRecord> => {
  const res: any = await OhsApiRequest(
    RequestType.Module,
    ModuleType.Incident,
    OhsApiRequestName.Fetch,
    { _id },
    { disableErrorToast }
  );
  if (!res) return new OhsIncidentRecord();

  let incidentRecord = {
    ...res,
    categoryCusvalsDefinition: res.categoryCusvals,
    propOrEnvDamageCusvalsDefinition: res.propOrEnvDamageCusvals,
    cusvalsDefinition: res.cusvals,
    locationCusvalsDefinition: res.locationCusvals,
    injuredPersonRoleCusvalsDefinition: res.injuredPersonRoleCusvals,
    categoryCusvals: [],
    propOrEnvDamageCusvals: [],
    cusvals: [],
    locationCusvals: [],
    injuredPersonRoleCusvals: [],
  };

  const categoryCusvals = fetchCusvalValue(incidentRecord.categoryCusvalsDefinition?._id || '');
  const propOrEnvDamageCusvals = fetchCusvalValue(
    incidentRecord.propOrEnvDamageCusvalsDefinition?._id || ''
  );
  const cusvals = fetchCusvalValue(incidentRecord.cusvalsDefinition?._id || '');
  const locationCusvals = fetchCusvalValue(incidentRecord.locationCusvalsDefinition?._id || '');
  const injuredPersonRoleCusvals = fetchCusvalValue(
    incidentRecord.injuredPersonRoleCusvalsDefinition?._id || ''
  );

  const [
    resCategoryCusvals,
    resPropOrEnvDamageCusvals,
    resCusvals,
    resLocationCusvals,
    resInjuredPersonRoleCusvals,
  ]: [OhsCusval[], OhsCusval[], OhsCusval[], OhsCusval[], OhsCusval[]] = await Promise.all([
    categoryCusvals,
    propOrEnvDamageCusvals,
    cusvals,
    locationCusvals,
    injuredPersonRoleCusvals,
  ]);
  incidentRecord = {
    ...incidentRecord,
    categoryCusvals: resCategoryCusvals || [],
    propOrEnvDamageCusvals: resPropOrEnvDamageCusvals || [],
    cusvals: resCusvals || [],
    locationCusvals: resLocationCusvals || [],
    injuredPersonRoleCusvals: resInjuredPersonRoleCusvals || [],
  };

  return incidentRecord;
};

export const getIncidentLists = (
  filterInfo: OhsIncidentFilterPayload
): Promise<ApiResListsType<any[]> | null> => {
  return OhsApiRequest(RequestType.Module, ModuleType.Incident, OhsApiRequestName.List, {
    filter: filterInfo,
  });
};

export const getIncidentSearchLists = (
  filterInfo: OhsIncidentSearchMain
): Promise<ApiResListsType<any[]> | null> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.Incident,
    OhsApiRequestName.Search,
    filterInfo
  );
};
export const getIncidentTaskRecord = async (
  _id: string,
  taskId: string
): Promise<OhsIncidentTaskRecord> => {
  const res: any = await OhsApiRequest(
    RequestType.Module,
    ModuleType.Task,
    OhsApiRequestName.Fetch,
    {
      _id: taskId,
      for: {
        _id,
        type: ModuleType.Incident,
      },
    }
  );
  if (!res) {
    return new OhsIncidentTaskRecord();
  }

  const newRes = {
    ...res,
    cusvalsDefinition: res.cusvals,
    hazardCategoryDefinition: res.hazardCategoryCusvals,
    severityDefinition: res.severityCusvals,
    controlReviewedDefinition: res.controlReviewCusvals,
    controlLevelDefinition: res.controlLevelCusvals,
    furtherInvestigationRequiredCusvalsDefinition:
      res.furtherInvestigationRequired && res.furtherInvestigationRequired.cusvals,
    cusvals: [],
    hazardCategoryCusvals: [],
    severityCusvals: [],
    controlReviewCusvals: [],
    controlLevelCusvals: [],
  };

  return newRes;
};

export const signOffIncident = (payload: any) =>
  OhsApiRequest(RequestType.Module, ModuleType.Incident, OhsApiRequestName.SignOff, payload);

export const editTaskIncident = (payload: any) =>
  OhsApiRequest(RequestType.Module, ModuleType.Incident, OhsApiRequestName.EditTask, payload);

export const archiveIncident = (payload: any, archived: boolean) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.Incident,
    archived ? OhsApiRequestName.Archive : OhsApiRequestName.UnArchive,
    payload
  );
};

export const deleteIncident = (incidentId: string) => {
  return OhsApiRequest(RequestType.Module, ModuleType.Incident, OhsApiRequestName.Delete, {
    _id: incidentId,
  });
};

export const moveIncident = (payload: any) => {
  return OhsApiRequest(RequestType.Module, ModuleType.Incident, OhsApiRequestName.Move, payload);
};

export const setConfidential = async (_id: string, isConfidential: boolean): Promise<any> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.Incident,
    isConfidential ? OhsApiRequestName.ConfidentialSet : OhsApiRequestName.ConfidentialUnSet,
    {
      _id,
    }
  );
};

export const listListUsersConfidentialAccess = () => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.Incident,
    OhsApiRequestName.ListUsersConfidentialAccess
  );
};

export const parseIncidentLookupList = (items: string[][]) => {
  const incidentLookupList = new Array<IncidentLookUp>();
  items.forEach((item) => {
    incidentLookupList.push(new IncidentLookUp(item));
  });
  return incidentLookupList;
};
